/**
 * Integration-owned event names.
 *
 * When the integration sends an event itself (window.b2mTrack / window.b2m.track
 * / b2mq), that name belongs to the integration: every copy the SDK would
 * generate on its own for the same name — DOM auto-tracking, platform trackers,
 * dataLayer-derived e-commerce events, GA-bridge mirrors — is skipped from then
 * on. Otherwise a site that starts sending `add_to_cart` with its own context
 * would be double counted by the SDK's automatic `add_to_cart`.
 *
 * Ownership is remembered for the browser session (sessionStorage) so a page
 * reload does not re-open the window between page load and the integration's
 * first manual call.
 *
 * Lifecycle events are never ownable: a single manual `page_view` must not
 * switch off automatic page-view tracking for the rest of the session.
 */

export const ORIGIN_KEY = '_origin';           // underscore → never serialised into event_params
export const ORIGIN_INTEGRATION = 'integration';
export const STORAGE_KEY = 'b2m_integration_events';

export const LIFECYCLE_EVENTS = new Set([
  'page_view', 'session_start', 'session_end',
  'user_identify', 'identity_reset', 'ab_test_view', 'platform_detected'
]);

/**
 * @param {string} name
 * @returns {boolean} can the integration take ownership of this event name?
 */
export function isOwnableEventName(name) {
  if (typeof name !== 'string' || !name) return false;
  if (LIFECYCLE_EVENTS.has(name)) return false;
  if (name.startsWith('chat_') || name.startsWith('$')) return false;
  return true;
}

export class IntegrationOwnedEvents {
  /**
   * @param {Storage|null} store  sessionStorage-like (getItem/setItem) or null
   */
  constructor(store = null) {
    this.store = store;
    this.names = new Set();
    this._load();
  }

  _load() {
    try {
      const raw = this.store && this.store.getItem(STORAGE_KEY);
      if (!raw) return;
      const list = JSON.parse(raw);
      if (Array.isArray(list)) list.forEach(n => { if (isOwnableEventName(n)) this.names.add(n); });
    } catch (e) { /* corrupt or unavailable storage — start empty */ }
  }

  _save() {
    try {
      if (this.store) this.store.setItem(STORAGE_KEY, JSON.stringify(Array.from(this.names)));
    } catch (e) { /* quota / privacy mode — in-memory only */ }
  }

  /**
   * Register a name as integration-owned.
   * @param {string} name
   * @returns {boolean} true when the name is (now) owned
   */
  add(name) {
    if (!isOwnableEventName(name)) return false;
    if (!this.names.has(name)) {
      this.names.add(name);
      this._save();
    }
    return true;
  }

  /** @param {string} name */
  has(name) {
    return this.names.has(name);
  }
}

/** Safe accessor: sessionStorage can throw in privacy modes / sandboxed frames. */
export function safeSessionStorage() {
  try {
    if (typeof window !== 'undefined' && window.sessionStorage) return window.sessionStorage;
  } catch (e) { /* blocked */ }
  return null;
}
